// src/features/admin/adminReviewSlice.js
import axios from "../../utils/axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { dedupeEntities } from "../../utils/collectionUtils";

// ────────────────────────────────────────────────
// Thunks – Admin-only review endpoints
// ────────────────────────────────────────────────

export const getAllReviews = createAsyncThunk(
  "adminReview/getAllReviews",
  async (
    { page = 1, limit = 20, direction, minRating, jobId, practitionerId, sort = "-createdAt" },
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit, sort };
      if (direction) params.direction = direction;
      if (minRating) params.minRating = minRating;
      if (jobId) params.jobId = jobId;
      if (practitionerId) params.practitionerId = practitionerId;

      const res = await axios.get("/reviews/admin", { params });
      return res.data.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch reviews"
      );
    }
  }
);

export const getReviewById = createAsyncThunk(
  "adminReview/getReviewById",
  async (reviewId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/reviews/admin/${reviewId}`);
      return res.data.data?.review || res.data.review;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch review details"
      );
    }
  }
);

export const toggleReviewVisibility = createAsyncThunk(
  "adminReview/toggleReviewVisibility",
  async ({ reviewId, visible }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/reviews/admin/${reviewId}/visibility`, {
        visible,
      });
      return res.data.data?.review || res.data.review;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update review visibility"
      );
    }
  }
);

export const getReviewStats = createAsyncThunk(
  "adminReview/getReviewStats",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get("/reviews/admin/stats");
      return res.data.data?.stats || res.data.stats;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch review statistics"
      );
    }
  }
);

/**
 * Optional: Fetch low-rated reviews (if you expose or add endpoint later)
 */
// export const getLowRatedReviews = createAsyncThunk(...);

/**
 * Optional: Fetch recent reviews (if needed in admin dashboard)
 */
// export const getRecentReviews = createAsyncThunk(...);

// ────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────

const initialState = {
  reviews: [],
  currentReview: null,
  total: 0,
  currentPage: 1,
  stats: {
    totalReviews: 0,
    directions: {
      practitioner_to_business: { count: 0, avgRating: 0 },
      business_to_practitioner: { count: 0, avgRating: 0 },
    },
    lowRatedReviews: 0,
  },
  loading: false,
  actionLoading: false,
  error: null,
  lastActionReviewId: null,
};

const adminReviewSlice = createSlice({
  name: "adminReview",
  initialState,
  reducers: {
    clearAdminReviewError: (state) => {
      state.error = null;
    },
    clearCurrentReview: (state) => {
      state.currentReview = null;
    },
  },
  extraReducers: (builder) => {
    // ── getAllReviews ───────────────────────────────────────
    builder
      .addCase(getAllReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = dedupeEntities(action.payload.reviews);
        state.total = action.payload.pagination?.total || 0;
        state.currentPage = action.payload.pagination?.page || 1;
      })
      .addCase(getAllReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── getReviewById ───────────────────────────────────────
    builder
      .addCase(getReviewById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReviewById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReview = action.payload;
      })
      .addCase(getReviewById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── toggleReviewVisibility ──────────────────────────────
    builder
      .addCase(toggleReviewVisibility.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionReviewId = action.meta.arg.reviewId;
        state.error = null;
      })
      .addCase(toggleReviewVisibility.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        // Update in list if present
        const index = state.reviews.findIndex((r) => r._id === id);
        if (index !== -1) {
          const revealedAt = state.reviews[index].revealedAt;
          state.reviews[index] = {
            ...state.reviews[index],
            ...updated,
            // Moderation visibility is independent from double-blind publication.
            revealedAt,
          };
        }

        // Update current if open
        if (state.currentReview?._id === id) {
          const revealedAt = state.currentReview.revealedAt;
          state.currentReview = {
            ...state.currentReview,
            ...updated,
            revealedAt,
          };
        }

        state.lastActionReviewId = null;
      })
      .addCase(toggleReviewVisibility.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
        state.lastActionReviewId = null;
      });

    // ── getReviewStats ──────────────────────────────────────
    builder
      .addCase(getReviewStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReviewStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(getReviewStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAdminReviewError, clearCurrentReview } =
  adminReviewSlice.actions;

// ── Selectors ─────────────────────────────────────────────
export const selectAdminReviews = (state) => state.adminReview.reviews;
export const selectAdminReviewsTotal = (state) => state.adminReview.total;
export const selectAdminReviewLoading = (state) => state.adminReview.loading;
export const selectAdminReviewActionLoading = (state) =>
  state.adminReview.actionLoading;
export const selectAdminReviewError = (state) => state.adminReview.error;
export const selectCurrentAdminReview = (state) =>
  state.adminReview.currentReview;
export const selectLastActionReviewId = (state) =>
  state.adminReview.lastActionReviewId;
export const selectAdminReviewStats = (state) => state.adminReview.stats;

export default adminReviewSlice.reducer;
